import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import Logo from "@/components/Logo";
import Footer from "@/components/Footer";
import { ArrowRight, Sparkles } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const statusTone = {
  on_track: "bg-[#EFF3EE] text-[#42624B] border-[#C5D5C8]",
  close_to_limit: "bg-[#F0EADA] text-[#7A5E1F] border-[#E4DFD2]",
  losing_money: "bg-[#FAEEED] text-[#7E332D] border-[#E7BFBA]",
};
const statusLabel = {
  on_track: "On Track",
  close_to_limit: "Close to Limit",
  losing_money: "Losing Money",
};

const fmtUsd = (n) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n || 0);

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const handleGoogle = () => {
  const redirect = window.location.origin + "/dashboard";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirect)}`;
};

const StickySignupBanner = () => (
  <div
    data-testid="sample-signup-banner"
    className="sticky top-0 z-30 bg-[#1F2326] text-[#F8F6F1] border-b border-[#2A2E32]"
  >
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-3 flex flex-col md:flex-row md:items-center gap-3">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 text-[11px] tracking-[0.1em] uppercase font-semibold text-[#B89B5E]">
          <Sparkles className="w-3.5 h-3.5" /> Viewing sample data
        </div>
        <div className="text-sm text-[#F8F6F1] mt-0.5">
          Add your own retainers to see which clients are on track, close to the limit, or losing money.
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={handleGoogle}
          data-testid="sample-banner-google"
          className="bg-white text-[#1F2326] hover:bg-[#F0EADA] px-3 py-2 rounded-md text-sm font-semibold transition-colors inline-flex items-center gap-2"
        >
          <svg className="w-4 h-4" viewBox="0 0 24 24" aria-hidden="true">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.76h3.56c2.08-1.92 3.28-4.74 3.28-8.09Z" />
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.56-2.76c-.99.67-2.26 1.06-3.72 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23Z" />
            <path fill="#FBBC05" d="M5.84 14.11A6.6 6.6 0 0 1 5.5 12c0-.73.13-1.44.34-2.11V7.05H2.18A11 11 0 0 0 1 12c0 1.78.43 3.46 1.18 4.95l3.66-2.84Z" />
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1A11 11 0 0 0 2.18 7.05l3.66 2.84C6.71 7.3 9.14 5.38 12 5.38Z" />
          </svg>
          Continue with Google
        </button>
        <Link
          to="/signup"
          data-testid="sample-banner-signup"
          className="bg-[#B89B5E] text-[#1F2326] hover:bg-[#A78946] px-3 py-2 rounded-md text-sm font-semibold transition-colors inline-flex items-center gap-1"
        >
          Create account <ArrowRight className="w-3.5 h-3.5" />
        </Link>
        <Link
          to="/login"
          data-testid="sample-banner-login"
          className="text-sm text-[#C9C2B0] hover:text-white px-2 py-2"
        >
          Log in
        </Link>
        <Link
          to="/pricing"
          data-testid="sample-banner-pricing"
          className="text-sm text-[#C9C2B0] hover:text-white px-2 py-2 hidden sm:inline"
        >
          View pricing
        </Link>
      </div>
    </div>
  </div>
);

const MetricCard = ({ label, value, hint }) => (
  <div className="bg-white border border-[#E4DFD2] rounded-xl p-5">
    <div className="text-[11px] uppercase tracking-wider font-semibold text-[#69747C]">{label}</div>
    <div className="font-display text-3xl font-semibold text-[#1F2326] mt-1 tabular-nums">{value}</div>
    {hint ? <div className="text-xs text-[#69747C] mt-1">{hint}</div> : null}
  </div>
);

const SampleDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    document.title = "Sample Dashboard · Retainer Tracker";
    if (user) {
      navigate("/dashboard", { replace: true });
      return;
    }
    (async () => {
      try {
        const r = await axios.get(`${BACKEND}/api/public/sample-dashboard`);
        setData(r.data);
      } catch (e) {
        setErr(e?.message || "Could not load sample data");
      }
    })();
  }, [user, navigate]);

  return (
    <div className="min-h-screen bg-[#F8F6F1] text-[#1F2326] flex flex-col" data-testid="sample-dashboard-page">
      <StickySignupBanner />
      <header className="border-b border-[#E4DFD2] bg-[#F8F6F1]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-4 flex items-center justify-between">
          <Link to="/"><Logo size={28} showTagline /></Link>
          <Link to="/pricing" className="text-sm text-[#69747C] hover:text-[#1F2326]">Pricing</Link>
        </div>
      </header>

      <main className="flex-1 max-w-7xl mx-auto w-full px-6 lg:px-10 py-10">
        <div className="text-[11px] tracking-[0.1em] uppercase font-semibold text-[#B89B5E]">
          Sample retainer dashboard
        </div>
        <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tighter text-[#1F2326] mt-2">
          This is what your dashboard looks like
        </h1>
        <p className="text-base text-[#69747C] mt-3 max-w-2xl">
          Five sample retainers. Real calculations. Add your own clients to see your numbers.
        </p>

        {err ? (
          <div data-testid="sample-error" className="mt-8 bg-[#FAEEED] border border-[#E7BFBA] text-[#7E332D] rounded-lg px-4 py-3 text-sm">
            Could not load sample data. {err}
          </div>
        ) : null}

        {data ? (
          <>
            <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-4">
              <MetricCard label="Retainer revenue" value={fmtUsd(data.metrics.total_revenue)} hint={`Across ${data.metrics.total_clients} retainers`} />
              <MetricCard label="Logged hours" value={`${data.metrics.total_logged_hours.toFixed(1)}h`} hint={`of ${data.metrics.total_included_hours.toFixed(1)}h included`} />
              <MetricCard label="Average utilization" value={`${data.metrics.avg_utilization.toFixed(0)}%`} hint="Of included hours" />
              <MetricCard label="Gross profit" value={fmtUsd(data.metrics.gross_profit)} hint="Revenue minus delivery cost" />
            </div>

            <div className="mt-8 bg-white border border-[#E4DFD2] rounded-xl overflow-hidden">
              <div className="px-5 py-4 border-b border-[#EDE7D6] flex items-center justify-between">
                <div className="font-display text-lg font-semibold text-[#1F2326]">Retainer clients</div>
                <div className="text-xs text-[#69747C]">{data.clients.length} retainers</div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="sample-clients-table">
                  <thead>
                    <tr className="text-[11px] tracking-wider uppercase text-[#69747C] bg-[#FBFAF6]/60">
                      <th className="text-left font-semibold py-3 px-5">Client</th>
                      <th className="text-left font-semibold py-3 px-3">Service</th>
                      <th className="text-right font-semibold py-3 px-3">Monthly Fee</th>
                      <th className="text-right font-semibold py-3 px-3">Hours Used</th>
                      <th className="text-right font-semibold py-3 px-3">Hours Left / Over</th>
                      <th className="text-right font-semibold py-3 px-3">Earned to Date</th>
                      <th className="text-right font-semibold py-3 px-3">Profit</th>
                      <th className="text-right font-semibold py-3 px-3">Margin</th>
                      <th className="text-left font-semibold py-3 px-3">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.clients.map((c) => {
                      const over = c.overage_hours > 0;
                      return (
                        <tr
                          key={c.id}
                          className="border-t border-[#EDE7D6] hover:bg-[#FBFAF6]/60 transition-colors"
                          data-testid={`sample-row-${c.id}`}
                        >
                          <td className="px-5 py-3 font-medium text-[#1F2326]">{c.name}</td>
                          <td className="px-3 py-3 text-[#69747C]">{c.service_type || "—"}</td>
                          <td className="px-3 py-3 text-right tabular-nums">{fmtUsd(c.monthly_fee)}</td>
                          <td className="px-3 py-3 text-right tabular-nums">
                            {c.logged_hours.toFixed(1)}h <span className="text-[#A9A294]">/ {c.included_hours.toFixed(0)}h</span>
                          </td>
                          <td className="px-3 py-3 text-right tabular-nums">
                            {over ? (
                              <span className="text-[#7E332D]">Over by {c.overage_hours.toFixed(1)}h</span>
                            ) : (
                              <span>{c.remaining_hours.toFixed(1)}h left</span>
                            )}
                          </td>
                          <td className="px-3 py-3 text-right tabular-nums">
                            {fmtUsd(c.earned_to_date)}
                            <div className="text-[10px] text-[#69747C]">{c.value_used_pct.toFixed(0)}% used</div>
                          </td>
                          <td className="px-3 py-3 text-right tabular-nums">{fmtUsd(c.estimated_profit)}</td>
                          <td className="px-3 py-3 text-right tabular-nums">{c.profit_margin.toFixed(0)}%</td>
                          <td className="px-3 py-3">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[10px] font-semibold uppercase tracking-wide ${statusTone[c.status]}`}>
                              {statusLabel[c.status]}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="mt-10 bg-[#1F2326] text-[#F8F6F1] rounded-2xl p-8 sm:p-10">
              <h2 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight">
                See your own retainers like this
              </h2>
              <p className="text-[#C9C2B0] mt-3 max-w-xl">
                Add your real clients in 5 minutes. Track hours, cost, profit, margin, and status without spreadsheets.
              </p>
              <div className="mt-5 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={handleGoogle}
                  data-testid="sample-cta-google"
                  className="bg-white text-[#1F2326] hover:bg-[#F0EADA] px-4 py-2.5 rounded-lg text-sm font-semibold transition-colors inline-flex items-center gap-2"
                >
                  Continue with Google
                </button>
                <Link
                  to="/signup"
                  data-testid="sample-cta-signup"
                  className="bg-[#B89B5E] text-[#1F2326] hover:bg-[#A78946] px-4 py-2.5 rounded-lg text-sm font-semibold transition-colors inline-flex items-center gap-2"
                >
                  Create account <ArrowRight className="w-4 h-4" />
                </Link>
                <Link
                  to="/login"
                  data-testid="sample-cta-login"
                  className="text-sm text-[#C9C2B0] hover:text-white px-3 py-2.5"
                >
                  Log in
                </Link>
                <Link
                  to="/pricing"
                  data-testid="sample-cta-pricing"
                  className="text-sm text-[#C9C2B0] hover:text-white px-3 py-2.5"
                >
                  View pricing
                </Link>
              </div>
              <div className="mt-3 text-xs text-[#C9C2B0]">
                14-day free trial. No credit card needed.
              </div>
            </div>
          </>
        ) : (
          !err && <div className="mt-10 text-sm text-[#69747C]">Loading sample data…</div>
        )}
      </main>

      <Footer />
    </div>
  );
};

export default SampleDashboard;
